/* eslint-disable no-console */
import { toast } from "react-toastify";
import BreadCrumb from "../../components/BreadCrumb";
import Layout from "../../components/Layout";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { httprequest } from "../../data/api";
import TabContext from "@mui/lab/TabContext";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useMediaQuery, Grid } from "@mui/material";
import useStyles from "./styles";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { TableSearch } from "../../components/Table/TableActions";
import Table from "../../components/Table/Table";
import { Icon } from "@iconify/react/dist/iconify.js";

const STUDENT_ENDPOINT = "/api/v1/students";
const GENERATE_PASSWORD = "/api/v1/generate-password";
const AUDIT_ENDPOINT = "/api/v1/password-audit-logs";

const StudentPassword = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const styles = useStyles();
  const [studentData, setStudentData] = useState({});
  const { id } = useParams();
  const [value, setValue] = useState("1");
  const [copyModal, setCopyModal] = useState(false);
  const [password, setPassword] = useState("");
  const [auditLogs, setAuditLogs] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [generationType, setGenerationType] = useState("auto");
  const [manualPassword, setManualPassword] = useState("");
  const [showManualPassword, setShowManualPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[a-zA-Z\d!@#$%^&*(),.?":{}|<>]{8,}$/;
  const passwordValidationMessage = "Password should be at least 8 characters, contain a lowercase, uppercase, a number and a special character";

  useEffect(() => {
    if (value === "2") {
      httprequest
        .get(`${AUDIT_ENDPOINT}?user_id=${studentData?.user_id}`)
        .then((res) => {
          setAuditLogs(res?.data?.data || []);
          setSearchResult(res?.data?.data);
        })
        .catch(() => {
          toast.error("Failed to fetch password logs");
        });
    }
  }, [value, studentData?.user_id]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleCopyModal = () => {
    setCopyModal(!copyModal);
  };

  useEffect(() => {
    httprequest.get(`${STUDENT_ENDPOINT}/${id}`)
      .then((res) => {
        setStudentData(res?.data?.data);
      }).catch((err) => {
        toast.error(err?.response?.data);
      });
  }, [id]);

  // Frontend password generation function

  const generateRandomPassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8}$/;

    let password = "";
    do {
      password = "";
      for (let i = 0; i < 8; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
      }
    } while (!regex.test(password));

    return password;
  };

  const submitPasswordToBackend = (passwordToSubmit) => {
    const payload = {
      user_id: studentData?.user_id,
      role: "student",
      password_input: passwordToSubmit
    };

    return httprequest
      .post(GENERATE_PASSWORD, payload)
      .then((res) => {
        toast.success(res?.data?.message || "Password saved successfully!");

        return true;
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error saving password");
        throw err;
      });
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(password);
    toast.success("Password copied to clipboard!");
    handleCopyModal();
    setManualPassword("");
  };

  const validateManualPassword = (password) => {
    return passwordRegex.test(password);
  };

  const handleManualPasswordChange = (e) => {
    const value = e.target.value;
    setManualPassword(value);

    if (value && !validateManualPassword(value)) {
      setPasswordError(passwordValidationMessage);
    } else {
      setPasswordError("");
    }
  };

  const handleGeneratePassword = () => {
    setIsGenerating(true);

    if (generationType === "auto") {
      const generatedPassword = generateRandomPassword(length);
      submitPasswordToBackend(generatedPassword)
        .then(() => {
          setPassword(generatedPassword);
          setCopyModal(true);
          handleCopyPassword();
        })
        .catch(() => {})
        .finally(() => {
          setIsGenerating(false);
        });
    } else {
      if (!manualPassword.trim()) {
        toast.error("Please enter a password");
        setIsGenerating(false);

        return;
      }

      if (!validateManualPassword(manualPassword)) {
        toast.error(passwordValidationMessage);
        setIsGenerating(false);

        return;
      }

      submitPasswordToBackend(manualPassword)
        .then(() => {
          setPassword(manualPassword);
          setCopyModal(true);
          handleCopyPassword();
        })
        .catch(() => {})
        .finally(() => {
          setIsGenerating(false);
        });
    }
  };

  const columns = [
    {
      name: "Generated By",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Generated By</p>}
          {row?.generated_by?.first_name} {row?.generated_by?.last_name}
        </>
      )
    },
    {
      name: "Target User",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Target User</p>}
          {row?.target_user?.first_name} {row?.target_user?.last_name}
        </>
      )
    },
    {
      name: "Target Email",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Target Email</p>}
          {row?.target_user?.email}
        </>
      )
    },
    {
      name: "Target Role",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Target Role</p>}
          {row?.role}
        </>
      )
    },
    {
      name: "Date",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Date</p>}
          {row?.created_at?.substring(0, 10)}
        </>
      )
    }
  ];

  const handleSearchAudit = (e) => {
    const { value } = e.target;
    const valArray = value.toLowerCase().split(" ");

    const result = auditLogs.filter((data) => valArray.every((word) => (data?.generated_by?.first_name?.toLowerCase().includes(word) ||
       data?.generated_by?.last_name?.toLowerCase().includes(word) ||
       data?.target_user?.first_name?.toLowerCase().includes(word) ||
       data?.target_user?.last_name?.toLowerCase().includes(word) ||
       data?.target_user?.email?.toLowerCase().includes(word))
    ));

    setSearchResult(result);
  };

  return (
    <Layout
      children={
        <>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/users/students"
                link_one_name="Students"
                active={`${studentData?.first_name} ${studentData?.last_name}`}
                description={`Manage Credentials of ${studentData?.first_name} ${studentData?.last_name}`}
              />
            </div>
          </div>
          <div className="page-content white_bg">
            <p className={styles.head}>Credentials Management</p>
            <TabContext value={value}>

              <div className="tablist-container">
                <Tabs className="overide-tablist" onChange={handleChange} aria-label="lab API tabs example">
                  <Tab label="Password Generation" value="1" />
                  <Tab label="Password Audit" value="2" />
                </Tabs>
              </div>

              <TabPanel value="1" className="tabpanel-overide">
                <h3 className={styles.heading}>Generate Password</h3>
                <p className={styles.subheading}>
                  Generate a secure password or reset a {studentData?.first_name} {studentData?.last_name} password.
                </p>

                <div className={styles.generationTypeSection}>
                  <div className={styles.generationTypeOptions}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        value="auto"
                        checked={generationType === "auto"}
                        onChange={(e) => {
                          console.log("Changing to auto", e.target.value);
                          setGenerationType(e.target.value);
                        }}
                      />
                      Auto Generate
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        value="manual"
                        checked={generationType === "manual"}
                        onChange={(e) => {
                          console.log("Changing to manual", e.target.value);
                          setGenerationType(e.target.value);
                        }}
                      />
                      Manual Input
                    </label>
                  </div>
                </div>

                {generationType === "auto" && (
                  <div className={styles.autoGenerateSection}>

                    <p className={styles.autoGenerateHint}>
                      Click the button below to generate a random password
                    </p>
                  </div>
                )}

                {generationType === "manual" && (
                  <div className={styles.manualInputSection}>
                    <Grid container spacing={2}>
                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <div className={styles.passwordInputWrapper}>
                          <label className={styles.inputLabel}>Enter Password</label>
                          <div className={styles.passwordInputContainer}>
                            <input
                              type={showManualPassword ? "text" : "password"}
                              className={styles.passwordInput}
                              value={manualPassword}
                              onChange={handleManualPasswordChange}
                              placeholder="Enter custom password (minimum 8 characters)"
                            />
                            <button
                              type="button"
                              className={styles.passwordToggle}
                              onClick={() => setShowManualPassword(!showManualPassword)}
                            >
                              <Icon
                                icon={showManualPassword ? "hugeicons:view" : "hugeicons:view-off"}
                                className={styles.eyeIcon}
                              />
                            </button>
                          </div>
                          {passwordError && (
                            <div className="text-danger">
                              {passwordError}
                            </div>
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                )}

                <div className={styles.generateButtonSection}>
                  <Button
                    variant="primary"
                    buttonSize="fluid"
                    color="btndefault"
                    buttonName={
                      generationType === "auto"
                        ? "Generate Random Password"
                        : "Set Password"
                    }
                    isLoading={isGenerating}
                    onClick={handleGeneratePassword}
                  />
                </div>
              </TabPanel>

              <TabPanel value="2" className="tabpanel-overide">
                <Table
                  data={searchResult}
                  columns={columns}
                  subHeader={true}
                  pagination
                  subHeaderComponent={
                    <div className="action_div">
                      <TableSearch
                        searchTableFunc={handleSearchAudit}
                        placeholder="Search here..."
                      />
                    </div>
                  }
                />
              </TabPanel>
            </TabContext>
          </div>

          {/* Copy Password Modal */}
          <Modal
            title="Your Password has been generated"
            subtitle="Please send to the user immediately because you won't be able to see this password again"
            modalContent={
              <>
                <div style={{ width: "100%", textAlign: "center", display: "flex", justifyContent: "center", marginBottom: 0 }} className={styles.passdiv}>
                  <h3 style={{ marginBlock: "1rem" }} className={styles.password}>{password}</h3>
                </div>
              </>
            }
            modalFooter={
              <>
                <Button
                  variant="primary"
                  buttonName="Copy"
                  buttonSize="full"
                  color="btndefault"
                  onClick={handleCopyPassword}
                />
              </>
            }
            isopen={copyModal}
            closeModal={handleCopyModal}
          />
          {/* <ToastContainer /> */}
        </>
      }
    />
  );
};

export default StudentPassword;
